import * as React from 'react';
import classNames from 'classnames';
import FormHelperText, { FormHelperTextProps } from '../FormHelperText';

export interface TextInputProps {
  name: string;
  type?: string;
  className?: string;
  wrapperClassName?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  helperText?: React.ReactNode | string;
  errorText?: string;
  autoComplete?: string;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  hasError?: boolean;
  size?: 'large';
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  cta?: React.ReactNode;
  placeholder?: string;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  // pass from register from react-hook-form
  value?: number | string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (props, ref) => {
    const {
      name,
      size,
      type = 'text',
      className,
      disabled,
      helperText,
      errorText,
      endAdornment = null,
      startAdornment = null,
      hasError = false,
      wrapperClassName,
      cta,
      formHelperTextProps,
      ...otherProps
    } = props;
    const helperTextId = `${name}-helper-text`;
    const inputClasses = classNames(className, 'ui-input', {
      'ui-input--disabled': disabled,
      'ui-input--error': hasError,
      [`ui-input--${size}`]: size,
    });
    const wrapperClasses = classNames({
      'ui-input-wrap--start-adornment': startAdornment,
      'ui-input-wrap--end-adornment': endAdornment,
      'ui-input-wrap--cta': cta,
    });
    const hasHelperText = helperText || hasError;
    const hasWrapper = startAdornment || endAdornment || cta;

    const checkAdornment = (object: {} | null) =>
      object && Object.keys(object).length !== 0;

    return (
      <div className={wrapperClassName}>
        {hasWrapper ? (
          <div className={wrapperClasses}>
            {checkAdornment(startAdornment) && (
              <span className="ui-input-start-adornment">{startAdornment}</span>
            )}
            <input
              id={name}
              type={type}
              name={name}
              disabled={disabled}
              className={inputClasses}
              aria-describedby={hasHelperText ? helperTextId : undefined}
              aria-invalid={hasError}
              {...otherProps}
              ref={ref}
            />
            {cta && <span className="ui-input-cta">{cta}</span>}
            {checkAdornment(endAdornment) && (
              <span className="ui-input-end-adornment">{endAdornment}</span>
            )}
          </div>
        ) : (
          <input
            id={name}
            type={type}
            name={name}
            disabled={disabled}
            className={inputClasses}
            aria-describedby={hasHelperText ? helperTextId : undefined}
            aria-invalid={hasError}
            {...otherProps}
            ref={ref}
          />
        )}

        {hasHelperText && (
          <FormHelperText
            hasError={hasError}
            helperTextId={helperTextId}
            text={hasError ? errorText : helperText}
            {...formHelperTextProps}
          />
        )}
      </div>
    );
  }
);

export default TextInput;
